import React, { useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import { fetchAlpacaPortfolioHistory } from '../../service/AlpacaService';

const PerformanceCards = ({ account, positions, orders }) => {
  const [portfolioHistory, setPortfolioHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPortfolioHistory();
  }, []);

  const loadPortfolioHistory = async () => {
    try {
      setLoading(true);
      // Fetch 1 month of history with 1 day resolution
      const data = await fetchAlpacaPortfolioHistory('1M', '1D');
      setPortfolioHistory(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading portfolio history:', error);
      setLoading(false);
    }
  };

  // Calculate metrics
  const calculateMetrics = () => {
    const now = new Date();
    const currentEquity = parseFloat(account?.equity || 0);
    const startingBalance = 100000; // $100k starting balance
    
    // Helper to get date range
    const getDateRange = (days) => {
      const start = new Date(now);
      start.setDate(start.getDate() - days);
      start.setHours(0, 0, 0, 0);
      return start;
    };
    
    // Get equity from portfolio history at a specific date
    const getEquityAtDate = (targetDate) => {
      if (!portfolioHistory || !portfolioHistory.timestamp || !portfolioHistory.equity) {
        return null;
      }
      
      const targetTimestamp = targetDate.getTime() / 1000; // Convert to seconds
      
      // Find the closest equity value on or before the target date
      let closestIndex = -1;
      let closestDiff = Infinity;
      
      for (let i = 0; i < portfolioHistory.timestamp.length; i++) {
        const timestamp = portfolioHistory.timestamp[i];
        const diff = targetTimestamp - timestamp;
        
        // We want the latest timestamp that's before or equal to target
        if (diff >= 0 && diff < closestDiff) {
          closestDiff = diff;
          closestIndex = i;
        }
      }
      
      if (closestIndex >= 0) {
        return parseFloat(portfolioHistory.equity[closestIndex]);
      }
      
      // If no data found before target date, return null
      return null;
    };
    
    // Filter orders by date range
    const filterOrdersByDate = (startDate) => {
      return orders.filter(order => {
        if (!order.filled_at) return false;
        const orderDate = new Date(order.filled_at);
        return orderDate >= startDate;
      });
    };
    
    // Calculate win rate based on completed trades (closed positions) within a specific period
    const calculateWinRate = (filteredOrders, periodStartDate) => {
      if (filteredOrders.length === 0) return 0;
      
      // To properly calculate P&L, we need ALL orders to track positions correctly,
      // but we only count wins/losses for sells that happened in the period
      
      // Group ALL orders by symbol (not just filtered ones)
      const symbolGroups = {};
      orders.forEach(order => {
        if (!order.filled_at) return;
        
        const symbol = order.symbol;
        if (!symbolGroups[symbol]) {
          symbolGroups[symbol] = [];
        }
        symbolGroups[symbol].push(order);
      });
      
      let wins = 0;
      let losses = 0;
      
      // For each symbol, process all orders chronologically
      Object.values(symbolGroups).forEach(symbolOrders => {
        // Sort by filled time
        symbolOrders.sort((a, b) => new Date(a.filled_at) - new Date(b.filled_at));
        
        let position = {
          qty: 0,
          totalCost: 0
        };
        
        symbolOrders.forEach(order => {
          const qty = parseFloat(order.qty || order.filled_qty || 0);
          const price = parseFloat(order.filled_avg_price || 0);
          const orderDate = new Date(order.filled_at);
          
          if (order.side === 'buy') {
            // Add to position
            position.totalCost += (qty * price);
            position.qty += qty;
          } else if (order.side === 'sell') {
            // Close position (or part of it)
            if (position.qty > 0) {
              const avgCost = position.totalCost / position.qty;
              const sellValue = qty * price;
              const costBasis = qty * avgCost;
              const pl = sellValue - costBasis;
              
              // Only count this trade if the sell happened within the period
              // Compare timestamps directly for accurate filtering
              const isInPeriod = periodStartDate 
                ? orderDate.getTime() >= periodStartDate.getTime() 
                : true;
              
              if (isInPeriod) {
                if (pl > 0.01) { // Use small threshold to avoid floating point issues
                  wins++;
                } else if (pl < -0.01) {
                  losses++;
                }
                // If pl ≈ 0, we don't count it as win or loss
              }
              
              // Update position (regardless of whether it's in period)
              const qtyToRemove = Math.min(qty, position.qty);
              position.qty -= qtyToRemove;
              position.totalCost -= (qtyToRemove * avgCost);
              
              // Handle case where position goes negative (short position)
              if (position.qty < -0.001) {
                position.qty = Math.abs(position.qty);
                position.totalCost = position.qty * price;
              }
            } else {
              // Opening a short position
              position.qty = -qty;
              position.totalCost = qty * price;
            }
          }
        });
      });
      
      const totalTrades = wins + losses;
      return totalTrades > 0 ? (wins / totalTrades) * 100 : 0;
    };
    
    // Get orders for each time period
    const todayOrders = filterOrdersByDate(getDateRange(1));
    const weekOrders = filterOrdersByDate(getDateRange(7));
    const monthOrders = filterOrdersByDate(getDateRange(30));
    
    // Get equity at start of each period from portfolio history
    const equityStartOfToday = getEquityAtDate(getDateRange(1)) || currentEquity;
    const equityStartOfWeek = getEquityAtDate(getDateRange(7)) || startingBalance;
    const equityStartOfMonth = getEquityAtDate(getDateRange(30)) || startingBalance;
    
    // Calculate P&L as change from start of period
    const todayPL = currentEquity - equityStartOfToday;
    const weekPL = currentEquity - equityStartOfWeek;
    const monthPL = currentEquity - equityStartOfMonth;
    const allTimePL = currentEquity - startingBalance;
    
    return {
      today: {
        pl: todayPL,
        plPercent: equityStartOfToday > 0 ? (todayPL / equityStartOfToday) * 100 : 0,
        trades: todayOrders.length,
        winRate: calculateWinRate(todayOrders, getDateRange(1))
      },
      week: {
        pl: weekPL,
        plPercent: equityStartOfWeek > 0 ? (weekPL / equityStartOfWeek) * 100 : 0,
        trades: weekOrders.length,
        winRate: calculateWinRate(weekOrders, getDateRange(7))
      },
      month: {
        pl: monthPL,
        plPercent: equityStartOfMonth > 0 ? (monthPL / equityStartOfMonth) * 100 : 0,
        trades: monthOrders.length,
        winRate: calculateWinRate(monthOrders, getDateRange(30))
      },
      allTime: {
        pl: allTimePL,
        plPercent: (allTimePL / startingBalance) * 100,
        trades: orders.length,
        winRate: calculateWinRate(orders, null) // null means all time
      }
    };
  };
  
  const metrics = calculateMetrics();
  
  // Show loading state while portfolio history is being fetched
  if (loading) {
    return (
      <div style={{ marginTop: '20px', marginBottom: '10px', textAlign: 'center' }}>
        <div className="spinner-border spinner-border-sm text-success mb-2" role="status"></div>
        <div style={{ color: '#888', fontSize: '14px' }}>Loading performance data...</div>
      </div>
    );
  }
  
  // Format currency
  const formatCurrency = (value) => {
    if (!value && value !== 0) return "$0.00";
    const sign = value >= 0 ? '+' : '';
    return sign + new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };
  
  // Format percentage
  const formatPercent = (value) => {
    const sign = value >= 0 ? '+' : '';
    return sign + value.toFixed(2) + '%';
  };
  
  // Card component
  const MetricCard = ({ title, data }) => {
    const isPositive = data.pl >= 0;
    
    return (
      <Col xs={12} sm={6} lg={3}>
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '15px',
          transition: 'transform 0.2s, border-color 0.2s'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.borderColor = isPositive ? '#10b981' : '#ef4444';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.borderColor = '#333';
        }}
        >
          {/* Title */}
          <div style={{ 
            color: '#888', 
            fontSize: '12px', 
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '12px'
          }}>
            {title}
          </div>
          
          {/* P&L Amount */}
          <div style={{ 
            color: isPositive ? '#10b981' : '#ef4444',
            fontSize: '24px',
            fontWeight: '700',
            marginBottom: '4px'
          }}>
            {formatCurrency(data.pl)}
          </div>
          
          {/* P&L Percentage */}
          <div style={{ 
            color: isPositive ? '#10b981' : '#ef4444',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '12px'
          }}>
            {formatPercent(data.plPercent)}
          </div>
          
          {/* Stats Row */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            paddingTop: '12px',
            borderTop: '1px solid #2a2a2a'
          }}>
            <div>
              <div style={{ color: '#666', fontSize: '11px' }}>Trades</div>
              <div style={{ color: '#e0e0e0', fontSize: '14px', fontWeight: '600' }}>
                {data.trades}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: '#666', fontSize: '11px' }}>Win Rate</div>
              <div style={{ color: '#e0e0e0', fontSize: '14px', fontWeight: '600' }}>
                {data.winRate.toFixed(0)}%
              </div>
            </div>
          </div>
        </div>
      </Col>
    );
  };
  
  return (
    <div style={{ marginTop: '20px', marginBottom: '10px' }}>
      <h3 style={{ color: '#e0e0e0', marginBottom: '15px', fontSize: '20px' }}>
        Performance Overview
      </h3>
      <Row>
        <MetricCard title="TODAY" data={metrics.today} />
        <MetricCard title="THIS WEEK" data={metrics.week} />
        <MetricCard title="THIS MONTH" data={metrics.month} />
        <MetricCard title="ALL TIME" data={metrics.allTime} />
      </Row>
    </div>
  );
};

export default PerformanceCards;